import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { UserPlus, Mail, Lock, User, AtSign, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const SC_BRUEHL_LOGO = 'https://scbruehl.ch/wp-content/themes/scbruehl/assets/img/scb_logo.svg';

export default function RegisterPage() {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [pseudonym, setPseudonym] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [amountPerGoal, setAmountPerGoal] = useState('');
    const [customAmount, setCustomAmount] = useState('');
    const [supportedPerson, setSupportedPerson] = useState('');
    const [address, setAddress] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const { user, loading } = useAuth();

    // Redirect already logged-in users
    if (!loading && user) {
        return <Navigate to="/" replace />;
    }

    const defaultPseudonym = `${firstName.trim()}_${lastName.trim()}`;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!firstName.trim() || !lastName.trim()) {
            toast.error('Bitte Vor- und Nachname eingeben');
            return;
        }

        if (password.length < 6) {
            toast.error('Passwort muss mindestens 6 Zeichen lang sein');
            return;
        }

        if (password !== passwordConfirm) {
            toast.error('Passwörter stimmen nicht überein');
            return;
        }

        // Validate amount
        const finalAmount = amountPerGoal === 'other' ? parseFloat(customAmount) : parseFloat(amountPerGoal);
        if (!amountPerGoal || (amountPerGoal === 'other' && (isNaN(finalAmount) || finalAmount < 1))) {
            toast.error('Bitte Betrag pro Tor auswählen');
            return;
        }

        setIsLoading(true);
        try {
            const finalPseudonym = pseudonym.trim() || defaultPseudonym;
            await axios.post(`${API_URL}/auth/register`, {
                email: email,
                password: password,
                first_name: firstName,
                last_name: lastName,
                pseudonym: finalPseudonym,
                amount_per_goal: finalAmount,
                supported_person: supportedPerson.trim() || null,
                address: address.trim() || null
            });
            setEmailSent(true);
        } catch (error) {
            const message = error.response?.data?.detail || 'Registrierung fehlgeschlagen';
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    // Email sent confirmation screen
    if (emailSent) {
        return (
            <div className="auth-background min-h-screen flex items-center justify-center p-4">
                <div className="w-full max-w-md animate-scale-in">
                    <div className="text-center mb-8">
                        <img src={SC_BRUEHL_LOGO} alt="SC Brühl St. Gallen" className="w-20 h-20 mx-auto mb-4" />
                        <h1 className="text-4xl font-heading text-white tracking-tight">Kronentipp</h1>
                        <p className="text-green-200 mt-2 font-body">SC Brühl St. Gallen - Tippspiel</p>
                    </div>
                    <Card className="glass-card border-white/10">
                        <CardContent className="pt-8 pb-8 text-center space-y-5">
                            <div className="flex justify-center">
                                <div className="w-16 h-16 rounded-full bg-[#00ad43]/20 flex items-center justify-center">
                                    <CheckCircle className="w-9 h-9 text-[#00ad43]" />
                                </div>
                            </div>
                            <h2 className="text-xl font-semibold" data-testid="email-sent-title">Bestätigungs-Email gesendet</h2>
                            <p className="text-muted-foreground text-sm leading-relaxed">
                                Wir haben eine Email an <strong>{email}</strong> gesendet. 
                                Bitte klicke auf den Bestätigungslink, um dein Konto zu aktivieren.
                            </p>
                            <p className="text-muted-foreground text-xs">
                                Der Link ist 48 Stunden gültig. Prüfe auch deinen Spam-Ordner.
                            </p>
                            <div className="pt-2">
                                <Link to="/login">
                                    <Button variant="outline" className="gap-2" data-testid="back-to-login-button">
                                        Zurück zur Anmeldung
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-background min-h-screen flex items-center justify-center p-4">
            <div className="w-full max-w-md animate-scale-in">
                <div className="text-center mb-8">
                    <img src={SC_BRUEHL_LOGO} alt="SC Brühl St. Gallen" className="w-20 h-20 mx-auto mb-4" />
                    <h1 className="text-4xl font-heading text-white tracking-tight">Kronentipp</h1>
                    <p className="text-green-200 mt-2 font-body">SC Brühl St. Gallen - Tippspiel</p>
                </div>

                <Card className="glass-card border-white/10">
                    <CardHeader className="space-y-1">
                        <CardTitle className="text-2xl text-center flex items-center justify-center gap-2">
                            <UserPlus className="w-6 h-6 text-[#00ad43]" />
                            Registrieren
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label htmlFor="firstName">Vorname *</Label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input
                                            id="firstName"
                                            type="text"
                                            placeholder="Max"
                                            value={firstName}
                                            onChange={(e) => setFirstName(e.target.value)}
                                            className="pl-10"
                                            required
                                            data-testid="register-firstname-input"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="lastName">Nachname *</Label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input
                                            id="lastName"
                                            type="text"
                                            placeholder="Muster"
                                            value={lastName}
                                            onChange={(e) => setLastName(e.target.value)}
                                            className="pl-10"
                                            required
                                            data-testid="register-lastname-input"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="pseudonym">Pseudonym</Label>
                                <div className="relative">
                                    <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        id="pseudonym"
                                        type="text"
                                        placeholder={firstName || lastName ? defaultPseudonym : 'max_muster'}
                                        value={pseudonym}
                                        onChange={(e) => setPseudonym(e.target.value)}
                                        className="pl-10"
                                        data-testid="register-pseudonym-input"
                                    />
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Wird in der Rangliste angezeigt. Standard: {firstName && lastName ? defaultPseudonym : 'vorname_nachname'}
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="address">Adresse / PLZ, Ort</Label>
                                <Input
                                    id="address"
                                    type="text"
                                    placeholder="Musterstrasse 1, 9000 St. Gallen"
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    data-testid="register-address-input"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="email">Email *</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="ihre@email.ch"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="pl-10"
                                        required
                                        data-testid="register-email-input"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password">Passwort *</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="Mindestens 6 Zeichen"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="pl-10"
                                        required
                                        data-testid="register-password-input"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="passwordConfirm">Passwort bestätigen *</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        id="passwordConfirm"
                                        type="password"
                                        placeholder="Passwort wiederholen"
                                        value={passwordConfirm}
                                        onChange={(e) => setPasswordConfirm(e.target.value)}
                                        className="pl-10"
                                        required
                                        data-testid="register-password-confirm-input"
                                    />
                                </div>
                            </div>

                            {/* Amount per goal (Pflichtfeld) */}
                            <div className="space-y-3">
                                <Label>Für jedes Tor, das die 1. Mannschaft in der Vorrunde 2026/27 erzielt, bezahle ich: *</Label>
                                <div className="flex flex-wrap gap-2">
                                    {['2', '5', '10', '20'].map(val => (
                                        <button
                                            key={val}
                                            type="button"
                                            onClick={() => { setAmountPerGoal(val); setCustomAmount(''); }}
                                            className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
                                                amountPerGoal === val 
                                                    ? 'bg-[#00ad43] text-white border-[#00ad43]' 
                                                    : 'bg-background border-input hover:bg-accent'
                                            }`}
                                            data-testid={`amount-${val}`}
                                        >
                                            CHF {val}
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => setAmountPerGoal('other')}
                                        className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
                                            amountPerGoal === 'other' 
                                                ? 'bg-[#00ad43] text-white border-[#00ad43]' 
                                                : 'bg-background border-input hover:bg-accent'
                                        }`}
                                        data-testid="amount-other"
                                    >
                                        Anderer Betrag
                                    </button>
                                </div>
                                {amountPerGoal === 'other' && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-muted-foreground">CHF</span>
                                        <Input
                                            type="number"
                                            min="1"
                                            step="0.50"
                                            placeholder="Betrag"
                                            value={customAmount}
                                            onChange={(e) => setCustomAmount(e.target.value)}
                                            className="w-28"
                                            data-testid="amount-custom-input"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Supported person */}
                            <div className="space-y-2">
                                <Label htmlFor="supportedPerson">Ich unterstütze mit meinem Tor-Beitrag folgenden Spieler / Trainer / Betreuer:</Label>
                                <Input
                                    id="supportedPerson"
                                    type="text"
                                    placeholder="Optional"
                                    value={supportedPerson}
                                    onChange={(e) => setSupportedPerson(e.target.value)}
                                    data-testid="register-supported-person-input"
                                />
                            </div>

                            <Button 
                                type="submit" 
                                className="w-full btn-primary"
                                disabled={isLoading}
                                data-testid="register-submit-button"
                            >
                                {isLoading ? (
                                    <span className="flex items-center gap-2">
                                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Registrieren...
                                    </span>
                                ) : (
                                    'Registrieren'
                                )}
                            </Button>
                        </form>

                        <div className="mt-6 text-center text-sm">
                            <span className="text-muted-foreground">Bereits ein Konto? </span>
                            <Link 
                                to="/login" 
                                className="text-primary hover:underline font-medium"
                                data-testid="login-link"
                            >
                                Jetzt anmelden
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
